// central factory for TanStack Query keys. always reference these instead
// of inlining string arrays so renames are typesafe and test setup can
// invalidate by a known key.

export const queryKeys = {

    auth: {
        me: () => ["auth", "me"] as const,
        ssoConfig: () => ["auth", "sso-config"] as const,
    },

    projects: {
        all: () => ["projects"] as const,
        list: () => ["projects", "list"] as const,
        detail: (id: string) => ["projects", "detail", id] as const,
    },

    scans: {
        all: () => ["scans"] as const,
        active: () => ["scans", "active"] as const,
        listForProject: (projectId: string) => ["scans", "for-project", projectId] as const,
        detail: (id: string) => ["scans", "detail", id] as const,
        pages: (scanId: string, filter?: Record<string, unknown>) =>
            filter
                ? (["scans", "detail", scanId, "pages", filter] as const)
                : (["scans", "detail", scanId, "pages"] as const),
        pageCategories: (scanId: string) => ["scans", "detail", scanId, "page-categories"] as const,
        pageStats: (scanId: string) => ["scans", "detail", scanId, "page-stats"] as const,
        assets: (scanId: string, filter?: Record<string, unknown>) =>
            filter
                ? (["scans", "detail", scanId, "assets", filter] as const)
                : (["scans", "detail", scanId, "assets"] as const),
        siteData: (scanId: string) => ["scans", "detail", scanId, "site-data"] as const,
        discoveryTree: (scanId: string) => ["scans", "detail", scanId, "discovery-tree"] as const,
        aiUsage: (scanId: string) => ["scans", "detail", scanId, "ai-usage"] as const,
        logs: (scanId: string, filter?: Record<string, unknown>) =>
            filter
                ? (["scans", "detail", scanId, "logs", filter] as const)
                : (["scans", "detail", scanId, "logs"] as const),
        forms: (scanId: string, filter?: Record<string, unknown>) =>
            filter
                ? (["scans", "detail", scanId, "forms", filter] as const)
                : (["scans", "detail", scanId, "forms"] as const),
        formDetail: (scanId: string, formId: string) =>
            ["scans", "detail", scanId, "forms", formId] as const,
        listings: (scanId: string, filter?: Record<string, unknown>) =>
            filter
                ? (["scans", "detail", scanId, "listings", filter] as const)
                : (["scans", "detail", scanId, "listings"] as const),
        listingDetail: (scanId: string, listingId: string) =>
            ["scans", "detail", scanId, "listings", listingId] as const,
        regions: (scanId: string) => ["scans", "detail", scanId, "regions"] as const,
        regionDetail: (scanId: string, regionId: string) =>
            ["scans", "detail", scanId, "regions", regionId] as const,
        regionPages: (scanId: string, regionId: string, page: number) =>
            ["scans", "detail", scanId, "region-pages", regionId, page] as const,
        aiFailures: (scanId: string) => ["scans", scanId, "ai-failures"] as const,
        navDistinct: (scanId: string) => ["scans", "detail", scanId, "nav-distinct"] as const,
        navPages: (scanId: string, repId: string, page: number) =>
            ["scans", "detail", scanId, "nav-pages", repId, page] as const,
        pageNav: (scanId: string, pageId: string) =>
            ["scans", "detail", scanId, "page-nav", pageId] as const,
        exportBundle: (scanId: string, includeExcluded: boolean, includeS3: boolean, includeRawHtml: boolean) =>
            ["scans", "detail", scanId, "export", { includeExcluded, includeS3, includeRawHtml }] as const,
    },

    pages: {
        detail: (pageId: string) => ["pages", "detail", pageId] as const,
        assets: (pageId: string) => ["pages", "detail", pageId, "assets"] as const,
        logs: (pageId: string, filter?: Record<string, unknown>) =>
            filter
                ? (["pages", "detail", pageId, "logs", filter] as const)
                : (["pages", "detail", pageId, "logs"] as const),
        forms: (pageId: string) => ["pages", "detail", pageId, "forms"] as const,
        listings: (pageId: string) => ["pages", "detail", pageId, "listings"] as const,
        hero: (pageId: string) => ["pages", "detail", pageId, "hero"] as const,
    },

    assets: {
        detail: (id: string) => ["assets", "detail", id] as const,
    },

    storage: {
        stats: () => ["storage", "stats"] as const,
        projectStats: (projectId: string) => ["storage", "stats", "project", projectId] as const,
        latestVerification: () => ["storage", "verification", "latest"] as const,
    },
};
